import { FC } from "react";
import { useTranslation } from "react-i18next";

import { RydeProgress } from "@ryde/controls";
import type { IInjectedDrawerProps, IProcessDrawerProps } from "@ryde/shared";

import { CommonDefaultDrawer } from "../common-default-drawer";
import { BaseDrawer, DrawerContentStyled, FormFooter } from "../drawer";
import {
  ProcessErrorsStrategy,
  ProcessProgressEndAdornment,
  ProcessStatisticStrategy,
  ProcessStatsFunnel,
} from "./components";
import { ProcessDrawerContext } from "./process-drawer-context";
import { ProcessWrapperStyled } from "./process.styled";
import { useProcessDrawer } from "./use-process-drawer";

interface IProcessDrawerPropsComputed
  extends IProcessDrawerProps, IInjectedDrawerProps {}

export const ProcessDrawer: FC<IProcessDrawerPropsComputed> = ({
  entity,
  processId,
  processTitle,
  onCloseDrawer,
  drawerNumber,
  currentEntityPage,
  avoidResetSelectedIds,
  companyId,
}) => {
  const { t } = useTranslation();
  const {
    progressValue,
    progressTitle,
    progressColor,
    currentProcess,
    isPending,
    isAborting,
    handleProcessAbort,
    onCloseHandler,
    cancelLabel,
  } = useProcessDrawer({
    processId,
    entity,
    processTitle,
    currentEntityPage,
    avoidResetSelectedIds,
    companyId,
    onCloseDrawer,
  });

  return (
    <ProcessDrawerContext.Provider value={{ currentProcess, entity }}>
      <BaseDrawer onCloseDrawer={onCloseHandler} drawerNumber={drawerNumber}>
        <CommonDefaultDrawer
          title={t("process.processDrawerTitle")}
          onCloseDrawer={onCloseHandler}
          Footer={
            <FormFooter
              cancelLabel={cancelLabel}
              onCancelClick={onCloseHandler}
              isLoading={isAborting}
            />
          }
        >
          <DrawerContentStyled>
            <ProcessWrapperStyled>
              <RydeProgress
                total={currentProcess?.affectedIds?.length}
                completed={currentProcess?.metadata?.metrics?.total}
                value={progressValue}
                title={progressTitle}
                color={progressColor}
                isLoading={isPending || isAborting}
                EndAdornment={
                  <ProcessProgressEndAdornment
                    isAbortVisible={isPending && !isAborting}
                    onAbort={handleProcessAbort}
                  />
                }
              />
              <ProcessStatsFunnel />
              <ProcessStatisticStrategy />
              <ProcessErrorsStrategy />
            </ProcessWrapperStyled>
          </DrawerContentStyled>
        </CommonDefaultDrawer>
      </BaseDrawer>
    </ProcessDrawerContext.Provider>
  );
};
